import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Eye, Tag, User, Clock, Share2 } from 'lucide-react';
import axios from 'axios';

const gold = '#C9A961';

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/blog/posts/${slug}`);
        setPost(res.data);
        // Set page title and meta for SEO
        document.title = `${res.data.meta_title || res.data.title} | Astrowins Blog`;
        // Update meta description
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', res.data.meta_description || res.data.excerpt || '');
        // Add JSON-LD structured data
        const jsonLd = {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": res.data.title,
          "description": res.data.meta_description || res.data.excerpt,
          "image": res.data.featured_image || "https://astrowins.in/logo.png",
          "author": { "@type": "Organization", "name": res.data.author || "Astrowins" },
          "publisher": { "@type": "Organization", "name": "Astrowins", "logo": { "@type": "ImageObject", "url": "https://astrowins.in/logo.png" } },
          "datePublished": res.data.published_at,
          "dateModified": res.data.updated_at || res.data.published_at,
          "mainEntityOfPage": `https://astrowins.in/blog/${slug}`,
        };
        let scriptTag = document.getElementById('blog-jsonld');
        if (!scriptTag) {
          scriptTag = document.createElement('script');
          scriptTag.id = 'blog-jsonld';
          scriptTag.type = 'application/ld+json';
          document.head.appendChild(scriptTag);
        }
        scriptTag.textContent = JSON.stringify(jsonLd);

        // Add FAQ schema if present
        if (res.data.schema_faq?.length > 0) {
          const faqLd = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": res.data.schema_faq.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": { "@type": "Answer", "text": faq.answer },
            })),
          };
          let faqScript = document.getElementById('blog-faq-jsonld');
          if (!faqScript) {
            faqScript = document.createElement('script');
            faqScript.id = 'blog-faq-jsonld';
            faqScript.type = 'application/ld+json';
            document.head.appendChild(faqScript);
          }
          faqScript.textContent = JSON.stringify(faqLd);
        }
      } catch (e) {
        setError('Post not found');
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      // Cleanup JSON-LD on unmount
      document.getElementById('blog-jsonld')?.remove();
      document.getElementById('blog-faq-jsonld')?.remove();
    };
  }, [slug]);

  const readTime = post ? Math.max(1, Math.round((post.content_html || '').replace(/<[^>]*>/g, '').split(/\s+/).length / 200)) : 0;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#080D26' }}>
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: `${gold} transparent ${gold} ${gold}` }} />
    </div>
  );

  if (error || !post) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#080D26' }}>
      <div className="text-center">
        <p className="text-slate-400 text-lg mb-4">{error || 'Post not found'}</p>
        <Link to="/blog" className="px-6 py-3 rounded-lg font-semibold" style={{ background: gold, color: '#080D26' }}>Back to Blog</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#080D26', fontFamily: 'Manrope, sans-serif' }}>
      <header className="border-b backdrop-blur-xl sticky top-0 z-50" style={{ background: 'rgba(8,13,38,0.92)', borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 text-white text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            <img src="/logo.png" alt="Astrowins" className="h-10 w-auto" />
            ASTROWINS
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/blog" className="text-sm font-medium" style={{ color: gold }}>Blog</Link>
            <Link to="/checkout" className="px-4 py-2 rounded-lg font-semibold text-sm" style={{ background: gold, color: '#080D26' }}>Get Started</Link>
          </div>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-5 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-slate-500 mb-6" data-testid="blog-breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link>
          <span>/</span>
          <Link to="/blog" className="hover:text-white">Blog</Link>
          <span>/</span>
          <span className="text-slate-300">{post.title}</span>
        </nav>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {post.category && (
            <Link to={`/blog?category=${post.category}`} className="text-xs font-bold uppercase tracking-wider mb-3 inline-block" style={{ color: gold }}>{post.category}</Link>
          )}

          <h1 data-testid="blog-post-title" className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-8">
            <span className="flex items-center gap-1.5"><User size={14} /> {post.author || 'Astrowins'}</span>
            <span className="flex items-center gap-1.5"><Calendar size={14} /> {post.published_at ? new Date(post.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> {readTime} min read</span>
            <span className="flex items-center gap-1.5"><Eye size={14} /> {post.views || 0} views</span>
          </div>

          {post.featured_image && (
            <div className="rounded-2xl overflow-hidden mb-8">
              <img src={post.featured_image} alt={post.title} className="w-full object-cover" style={{ maxHeight: '480px' }} />
            </div>
          )}

          {/* Blog content */}
          <div data-testid="blog-post-content"
            className="prose prose-invert prose-lg max-w-none"
            style={{ color: '#D1D5DB', lineHeight: 1.85, fontSize: '1.05rem' }}
            dangerouslySetInnerHTML={{ __html: post.content_html }}
          />

          {/* FAQ Section */}
          {post.schema_faq?.length > 0 && (
            <div className="mt-12 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>Frequently Asked Questions</h2>
              <div className="space-y-5">
                {post.schema_faq.map((faq, i) => (
                  <div key={i} className="rounded-xl p-6" style={{ background: '#101632', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <h3 className="text-white font-bold text-lg mb-3">{faq.question}</h3>
                    <p className="text-slate-300 text-base leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8">
              {post.tags.map(tag => (
                <span key={tag} className="px-3 py-1 rounded-full text-xs border border-slate-700 text-slate-400">
                  <Tag size={10} className="inline mr-1" />{tag}
                </span>
              ))}
            </div>
          )}

          {/* CTA — Free Calculator Visual */}
          <div className="mt-12 rounded-2xl overflow-hidden" style={{ border: `2px solid ${gold}` }}>
            <div className="p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(201,169,97,0.12), rgba(10,15,42,0.95))' }}>
              <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: gold }}>Try It Free — No Sign Up Needed</p>
              <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
                Curious about your numbers?
              </h3>
              <p className="text-slate-400 text-base mb-6 max-w-lg mx-auto">
                Check your Name Number, Driver & Conductor, or Lucky Mobile Number — free, instant results on our homepage.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <Link to="/" onClick={() => setTimeout(() => document.getElementById('free-calculators')?.scrollIntoView({ behavior: 'smooth' }), 500)} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-base" style={{ background: gold, color: '#080D26' }}>
                  Try Free Calculators
                </Link>
                <Link to="/checkout?plan=bundle" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm border" style={{ borderColor: 'rgba(201,169,97,0.4)', color: gold }}>
                  Get Full Access — Just ₹1,499
                </Link>
              </div>
            </div>
          </div>

          {/* Related Posts */}
          {post.related_posts?.length > 0 && (
            <div className="mt-12">
              <h2 className="text-xl font-bold text-white mb-5">Related Articles</h2>
              <div className="grid md:grid-cols-3 gap-4">
                {post.related_posts.map(rp => (
                  <Link key={rp.id} to={`/blog/${rp.slug}`} className="rounded-xl p-4 hover:scale-[1.02] transition-all" style={{ background: '#101632', border: '1px solid rgba(255,255,255,0.06)' }}>
                    {rp.featured_image && <img src={rp.featured_image} alt={rp.title} className="w-full h-32 object-cover rounded-lg mb-3" loading="lazy" />}
                    <h3 className="text-white font-semibold text-sm line-clamp-2">{rp.title}</h3>
                    <p className="text-slate-500 text-xs mt-1">{rp.published_at ? new Date(rp.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </article>
    </div>
  );
}
